import { ClientRepo } from "@services/clients/client.repo";
import { DropdownOptionsService } from "@services/clients/dropdownOptions.service";
import * as moment from "moment";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { ClientDetailsService } from "@services/clients/clients.details/clientdetails.service";
import { UserItem } from "@models/clients/userItem";

export class ClientDetailsGeneralController {
    public client: any;
    public months: any[] = [];
    public yearOptions: number[] = [];
    public isLoading: boolean = false;
    
    // Properties passed from parent
    public onSave: () => void;
    public isSaving: boolean = false;
    public lastSaved: Date | null = null;

    public dropdownOptions: any = {};

    // User management properties
    public users: UserItem[] = [];
    public selectedAdmin: UserItem | null = null;
    public selectedRelationshipManager: UserItem | null = null;
    public selectedInvestmentOfficer: UserItem | null = null;
    public isLoadingUsers: boolean = true;
    public onUpdateAdmin: (params: { user: UserItem }) => void;
    public onUpdateRelationshipManager: (params: { user: UserItem }) => void;
    public onUpdateInvestmentOfficer: (params: { user: UserItem }) => void;
    public onFilterUsers: (params: { searchText: string }) => UserItem[];
    
    // Search text for autocomplete
    public searchTextRelationshipManager: string = '';
    public searchTextInvestmentOfficer: string = '';
    public searchTextAdmin: string = '';

    /** @ngInject */
    constructor(
        private clientRepo: ClientRepo,
        private clientCRMRepo: ClientCRMRepo,
        private dropdownOptionsService: DropdownOptionsService,
        private clientDetailsService: ClientDetailsService,
        private $rootScope: angular.IRootScopeService,
        private $scope: angular.IScope,
        private notifyjs: any
    ) {}

    public $onInit(): void {
        // Initialize months data
        this.months = moment.months().map((month, index) => {
            return {
                id: index + 1,
                name: month
            }
        });
        
        // Initialize year options (1920 to current year, descending order)
        const currentYear = new Date().getFullYear();
        for (let year = currentYear; year >= 1920; year--) {
            this.yearOptions.push(year);
        }
        
        // Show loading if client data isn't available yet
        this.isLoading = !this.client;
        
        // // Listen for dropdown options loaded event from parent
        // this.$scope.$on('dropdownOptionsLoaded', (event: any, options: any) => {
        //     this.loadDropdownOptions(options);
        // });
        
        // Also try to get options immediately if they're already loaded
        this.dropdownOptionsService.getAllOptions()
            .then((options) => {
                this.dropdownOptions = options;
            })
            .catch((error) => {
                console.warn('Failed to get dropdown options immediately:', error);
            });

        // Watch for client number changes to validate it doesn't start with 0
        this.$scope.$watch(() => this.client && this.client.clientNumber, (newValue: string, oldValue: string) => {
            if (newValue && newValue.startsWith('0')) {
                this.notifyjs("Client number cannot start with 0", "error");
                // Reset to old value if it starts with 0
                if (this.client) {
                    this.client.clientNumber = oldValue || '';
                }
            }
        });
    }

    public $onChanges(changes: any): void {
        if (changes.client && changes.client.currentValue) {
            this.isLoading = false; // Hide loading when client data arrives
        }
    }

    public isIndividual(): boolean {
        return this.clientDetailsService.isIndividual(this.client);
    }

    public isInstitution(): boolean {
        return this.clientDetailsService.isInstitution(this.client);
    }

    public onSaveClick(): void {
        // Call the parent save function
        if (this.onSave) {
            this.onSave();
        }
    }

    public onKeyPress(event: KeyboardEvent): boolean {
        const target = event.target as HTMLInputElement;
        const currentValue = target.value || '';
        const selectionStart = target.selectionStart || 0;
        
        // Allow: backspace, delete, tab, escape, enter
        if ([8, 9, 27, 13].indexOf(event.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (event.keyCode === 65 && event.ctrlKey === true) ||
            (event.keyCode === 67 && event.ctrlKey === true) ||
            (event.keyCode === 86 && event.ctrlKey === true) ||
            (event.keyCode === 88 && event.ctrlKey === true)) {
            return true;
        }
        
        // Prevent input if already at 6 characters (unless it's a deletion key)
        if (currentValue.length >= 6 && [8, 46].indexOf(event.keyCode) === -1) {
            event.preventDefault();
            return false;
        }
        
        // Only allow numeric keys: 0-9 (keyCode 48-57) and numpad 0-9 (keyCode 96-105)
        if (event.keyCode >= 48 && event.keyCode <= 57) {
            // Prevent entering 0 as the first character
            if (event.keyCode === 48 || event.keyCode === 96) { // 0 key
                if (currentValue.length === 0 || selectionStart === 0) {
                    // If field is empty or cursor is at the start, prevent entering 0
                    event.preventDefault();
                    this.notifyjs("Client number cannot start with 0", "error");
                    return false;
                }
            }
            return true;
        }
        
        // Block everything else (letters, symbols, etc.)
        event.preventDefault();
        return false;
    }

    public onPaste(event: ClipboardEvent): void {
        const target = event.target as HTMLInputElement;
        const pastedText = (event.clipboardData || (window as any).clipboardData).getData('text');
        
        // Check if pasted text starts with 0
        if (pastedText && pastedText.trim().startsWith('0')) {
            event.preventDefault();
            this.notifyjs("Client number cannot start with 0", "error");
            return;
        }
        
        // Allow the paste to proceed normally
        // The watcher will catch any issues after paste
    }

    public updateAdministeredBy(user: UserItem): void {
        if (user && this.onUpdateAdmin) {
            this.onUpdateAdmin({ user: user });
        }
    }

    public updateRelationshipManager(user: UserItem): void {
        if (user && this.onUpdateRelationshipManager) {
            this.onUpdateRelationshipManager({ user: user });
        }        
    }

    public updateInvestmentOfficer(user: UserItem): void {
        if (user && this.onUpdateInvestmentOfficer) {
            this.onUpdateInvestmentOfficer({ user: user });
        }        
    }

    public filterUsers(searchText: string): UserItem[] {
        if (this.onFilterUsers) {
            return this.onFilterUsers({ searchText: searchText });
        }
        return [];
    }
}

export const ClientDetailsGeneralComponent: angular.IComponentOptions = {
    template: require('./clientdetails.general.html'),
    controller: ClientDetailsGeneralController,
    controllerAs: '$ctrl',
    bindings: {
        client: '<',
        onSave: '&',
        isSaving: '<',
        lastSaved: '<',
        users: '<',
        selectedAdmin: '=',
        selectedRelationshipManager: '=',
        selectedInvestmentOfficer: '=',
        onUpdateAdmin: '&',
        onUpdateRelationshipManager: '&',
        onUpdateInvestmentOfficer: '&',
        onFilterUsers: '&',
        isLoadingUsers: '<',
        isViewOnly: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/general/clientdetails.general.ts